import katex from 'katex';
import { marked } from 'marked';
import { sanitizeHtml } from './safeHtml';

const MATH_PLACEHOLDER = (index) => `%%KATEX${index}%%`;
const CODE_PLACEHOLDER = (index) => `%%CODEBLOCK${index}%%`;

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const renderLatex = (math, displayMode) => {
  try {
    return katex.renderToString(math, {
      throwOnError: false,
      displayMode,
      output: 'html',
      strict: 'ignore',
    });
  } catch (_error) {
    return `<code>${escapeHtml(math)}</code>`;
  }
};

export const normalizeCopilotMarkdown = (input) => {
  let text = String(input || '').replace(/\r\n/g, '\n');

  text = text.replace(/(\*\*[^*]+?\*\*)(?=\S)/g, '$1 ');
  text = text.replace(/(^|\n)(\d+)\.\s+\*\*([^*]+)\*\*[ \t]*/g, '\n\n**$2. $3**\n\n');
  text = text.replace(/(^|\n+)(\*\*\s*\d+\.\s*[^*]+?\*\*)[ \t]*/g, '\n\n$2\n\n');
  text = text.replace(/\*\*\s*(\d+)\.\s+/g, '**$1. ');
  text = text.replace(/\n[\t ]*[-•]\s/g, '\n* ');
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
};

const extractProtectedSegments = (text) => {
  const codeBlocks = [];
  const mathBlocks = [];

  let next = text.replace(/```[\s\S]*?```/g, (block) => {
    const token = CODE_PLACEHOLDER(codeBlocks.length);
    codeBlocks.push(block);
    return token;
  });

  next = next.replace(/`[^`\n]+`/g, (block) => {
    const token = CODE_PLACEHOLDER(codeBlocks.length);
    codeBlocks.push(block);
    return token;
  });

  const stashMath = (math, displayMode) => {
    const token = MATH_PLACEHOLDER(mathBlocks.length);
    mathBlocks.push({ math: math.trim(), displayMode });
    return token;
  };

  next = next.replace(/\$\$([\s\S]+?)\$\$/g, (_, math) => stashMath(math, true));
  next = next.replace(/\\\[([\s\S]+?)\\\]/g, (_, math) => stashMath(math, true));
  next = next.replace(/\\\(([\s\S]+?)\\\)/g, (_, math) => stashMath(math, false));
  next = next.replace(/\$([^$\n]+?)\$/g, (_, math) => stashMath(math, false));

  return { text: next, codeBlocks, mathBlocks };
};

const restoreCodeBlocks = (text, codeBlocks) =>
  text.replace(/%%CODEBLOCK(\d+)%%/g, (_, index) => codeBlocks[Number(index)] || '');

const restoreMathBlocks = (html, mathBlocks) =>
  html.replace(/%%KATEX(\d+)%%/g, (_, index) => {
    const block = mathBlocks[Number(index)];
    if (!block) return '';
    return renderLatex(block.math, block.displayMode);
  });

const wrapTables = (html) =>
  String(html)
    .replace(/<table>/g, '<div class="copilot-table-wrap"><table>')
    .replace(/<\/table>/g, '</table></div>');

export const renderCopilotMarkdownToSafeHtml = (input) => {
  const normalizedInput = normalizeCopilotMarkdown(input);
  if (!normalizedInput) {
    return '';
  }

  const prepared = extractProtectedSegments(normalizedInput);
  let text = restoreCodeBlocks(prepared.text, prepared.codeBlocks);
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  const html = wrapTables(
    restoreMathBlocks(String(marked.parse(text)), prepared.mathBlocks)
  ).replace(/<strong>\s+/g, '<strong>').replace(/\s+<\/strong>/g, '</strong>');

  return sanitizeHtml(html, {
    allowedAttr: [
      'class',
      'colspan',
      'data-comment-id',
      'data-tag',
      'data-username',
      'href',
      'rel',
      'rowspan',
      'target',
      'title',
      'style',
      'aria-hidden',
    ],
    forbidAttr: [],
  });
};

export const escapeCopilotPlainText = (input) => escapeHtml(String(input || '').trim());
